/**
 * ============================================================================
 * Reviews.io API Integration
 * ============================================================================
 *
 * This script handles fetching and displaying product reviews from Reviews.io
 * API in a carousel format. It includes functionality for:
 * - Fetching reviews based on product SKU
 * - Displaying reviews in a Slick carousel
 * - Showing reviews in a modal
 * - Text truncation with "show more/less" toggle
 * - Time formatting in Italian
 */

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Formats a date string into Italian time-ago format
 * @param {string} dateString - Date string from Reviews.io API
 * @returns {string} Formatted time ago string (e.g., "2 giorni fa")
 */
function formatTimeAgo(dateString) {
  const minutesAgo = Math.floor(
    (Date.now() - new Date(dateString.replace(" ", "T"))) / 60000
  );

  // Less than 2 minutes
  if (minutesAgo < 2) return "1 minuto fa";
  // Less than 1 hour
  if (minutesAgo < 60) return `${minutesAgo} minuti fa`;
  // Less than 2 hours
  if (minutesAgo < 120) return "1 ora fa";
  // Less than 1 day
  if (minutesAgo < 1440) return `${Math.floor(minutesAgo / 60)} ore fa`;
  // Less than 2 days
  if (minutesAgo < 2880) return "1 giorno fa";
  // Less than 1 week
  if (minutesAgo < 10080) return `${Math.floor(minutesAgo / 1440)} giorni fa`;
  // Less than 2 weeks
  if (minutesAgo < 20160) return "1 settimana fa";
  // Less than 1 month
  if (minutesAgo < 43200)
    return `${Math.floor(minutesAgo / 10080)} settimane fa`;
  // Less than 2 months
  if (minutesAgo < 86400) return "1 mese fa";
  // 2+ months
  return `${Math.floor(minutesAgo / 43200)} mesi fa`;
}

/**
 * Creates a toggle handler for expanding/collapsing review text
 * @param {HTMLElement} cardEl - The review card element
 * @param {number} wordLimit - Number of words to show when collapsed
 * @returns {Function} Event handler function
 */
function handleToggle(cardEl, wordLimit) {
  return function () {
    const span = this.querySelector("span");
    const contentEl = cardEl.querySelector(".review_carousel__content");
    const fullText = contentEl.dataset.full;

    // Toggle expanded state
    const isExpanded = cardEl.classList.toggle("expanded");
    contentEl.textContent = isExpanded
      ? fullText
      : getTruncatedText(fullText, wordLimit);

    span.textContent = isExpanded ? "Mostra di meno" : "Mostra di più";
  };
}

/**
 * Formats reviewer name based on show_family_name setting
 * @param {string} reviewerName - Full reviewer name
 * @param {string} show_family_name - "true" or "false" string
 * @returns {string} Formatted name (full or first name + last initial)
 */
function formatReviewerName(reviewerName, show_family_name) {
  if (show_family_name == "true") return reviewerName;

  const parts = reviewerName.trim().split(" ");
  if (parts.length < 2) return reviewerName;

  const firstName = parts[0];
  const lastInitial = parts[1].charAt(0).toUpperCase() + ".";

  return `${firstName} ${lastInitial}`;
}

/**
 * Truncates text to specified word limit
 * @param {string} text - Text to truncate
 * @param {number} wordLimit - Maximum number of words
 * @returns {string} Truncated text with "..." if needed
 */
function getTruncatedText(text, wordLimit) {
  const words = text.trim().split(" ");
  return words.length > wordLimit
    ? words.slice(0, wordLimit).join(" ") + "..."
    : text;
}

// ============================================================================
// Main Initialization
// ============================================================================

document.addEventListener("DOMContentLoaded", function () {
  // Find all review carousel instances on the page
  document.querySelectorAll(".reviewsio-carousel").forEach((el) => {
    // Extract configuration from data attributes
    const {
      store,
      sku,
      min_rating: minRatingRaw,
      total_reviews: totalReviewsRaw,
      empty_comments,
      show_family_name,
      show_anonymous_reviews,
      number_of_words: wordLimit,
    } = el.dataset;

    // Parse numeric settings as integers with default fallbacks
    const min_rating = parseInt(minRatingRaw) || 1;
    const total_reviews = parseInt(totalReviewsRaw) || 100;
    const number_of_words = parseInt(wordLimit) || 20;

    // Get DOM elements
    const track = el.querySelector(".review_carousel__track");
    const modalContainer = document.getElementById(
      "custom_reviewio_modal_reviews"
    );

    // Clear existing content
    if (track) track.innerHTML = "";
    if (modalContainer) modalContainer.innerHTML = "";

    // Build API URL with query parameters
    const apiUrl = `https://api.reviews.co.uk/reviews?store=${store}&sku=${sku}&per_page=${total_reviews}&min_rating=${min_rating}&type=product_review`;

    // Fetch reviews from Reviews.io API
    fetch(apiUrl)
      .then((res) => res.json())
      .then((data) => {
        const reviews = data.reviews || [];

        // Handle empty reviews case
        if (reviews.length === 0) {
          track.innerHTML =
            "<p style='color:white;'>Nessuna recensione disponibile per questo prodotto.</p>";
          return;
        }

        // Process each review
        reviews.forEach((r) => {
          const isEmptyComment = !r.comments || r.comments.trim() === "";
          const location = r.author.location ? r.author.location + " - " : "";

          // Filter reviews based on settings
          // Client-side min_rating filter (backup if API doesn't respect it)
          if (r.rating < min_rating) return;
          if (empty_comments === "false" && isEmptyComment) return;
          if (show_anonymous_reviews === "false" && r.author.name === "Anonimo")
            return;

          // Check if review text needs truncation
          const words = r.comments.trim().split(" ");
          const isLong = words.length > number_of_words;

          // Generate star rating HTML (★ for filled, ☆ for empty)
          const stars =
            `
            <svg xmlns="http://www.w3.org/2000/svg" width="23" height="22" viewBox="0 0 23 22" fill="none">
              <path d="M11.4126 0L14.1068 8.2918H22.8253L15.7719 13.4164L18.466 21.7082L11.4126 16.5836L4.35917 21.7082L7.05334 13.4164L-8.01086e-05 8.2918H8.71843L11.4126 0Z" fill="#FAC800"/>
            </svg>
            `.repeat(r.rating) +
            `
            <svg xmlns="http://www.w3.org/2000/svg" width="23" height="22" viewBox="0 0 23 22" fill="none">
              <defs>
                <mask id="starMask">
                  <path d="M11.4126 0L14.1068 8.2918H22.8253L15.7719 13.4164L18.466 21.7082L11.4126 16.5836L4.35917 21.7082L7.05334 13.4164L-8.01086e-05 8.2918H8.71843L11.4126 0Z"
                        fill="white"/>
                </mask>
              </defs>
              <path
                d="M11.4126 0L14.1068 8.2918H22.8253L15.7719 13.4164L18.466 21.7082L11.4126 16.5836L4.35917 21.7082L7.05334 13.4164L-8.01086e-05 8.2918H8.71843L11.4126 0Z"
                fill="none"
                stroke="#FAC800"
                stroke-width="2.5"
                vector-effect="non-scaling-stroke"
                mask="url(#starMask)"
              />
            </svg>
            `.repeat(5 - r.rating);

          // Generate verified badge HTML if review is verified
          const verifiedHtml =
            r.verified === 1
              ? `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M9 0C13.9706 4.15903e-07 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 4.3454e-07 13.9706 0 9C4.15903e-07 4.02944 4.02944 4.3454e-07 9 0ZM9 2C5.13401 2 2 5.13401 2 9C2 12.866 5.13401 16 9 16C12.866 16 16 12.866 16 9C16 5.13401 12.866 2 9 2ZM12.7461 5.34375C13.1087 4.92731 13.7398 4.88354 14.1562 5.24609C14.5727 5.60875 14.6165 6.23975 14.2539 6.65625L9.0293 12.6562C8.85269 12.859 8.60147 12.9824 8.33301 12.998C8.06473 13.0136 7.80144 12.9209 7.60254 12.7402L4.57715 9.99023C4.16851 9.61874 4.13831 8.98581 4.50977 8.57715C4.88126 8.16851 5.51419 8.13831 5.92285 8.50977L8.19141 10.5723L12.7461 5.34375Z" fill="#142745"/>
                  </svg>
              Cliente verificato`
              : "";

          // Create review card element
          const card = document.createElement("div");
          card.classList.add("review-card");
          card.innerHTML = `
                        <div class="review_carousel__header">
                            <div class="review_carousel__header-wrapper">
                              <div class="review_carousel__name">${formatReviewerName(
                                r.author.name,
                                show_family_name
                              )}
                              </div>
                              <div class="review_carousel__verified">${verifiedHtml}</div>
                            </div>
                            <div class="review_carousel__info_group">
                                <div class="review_carousel__stars">${stars}</div>
                            </div>
                        </div>
                        <div class="review_carousel__content" data-full="${
                          r.comments || ""
                        }">
                          ${getTruncatedText(r.comments || "", number_of_words)}
                        </div>
                        <div class="review_carosel__footer">
                            <div class="review_carousel__date">
                            <!-- ${location.replace(/"/g, "")} -->
                            ${formatTimeAgo(r.date_created)}</div>
                            
                            ${
                              isLong
                                ? `
                            <div class="review_carousel__more"><span>Mostra di più</span>
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none">
                              <path d="M1 1L7 7L13 1" stroke="#142745" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            </div>`
                                : ""
                            }
                        </div>

                    `;

          // Add card to carousel track
          track.appendChild(card);

          // Attach toggle handler for "show more/less" button
          card
            .querySelector(".review_carousel__more")
            ?.addEventListener("click", handleToggle(card, number_of_words));

          // Clone card for modal
          const clonedCard = card.cloneNode(true);
          modalContainer.appendChild(clonedCard);
          clonedCard
            .querySelector(".review_carousel__more")
            ?.addEventListener("click", handleToggle(clonedCard, number_of_words));
        });

        // Hide carousel while initializing Slick
        el.style.visibility = "hidden";
        el.style.display = "block";

        // Initialize Slick carousel
        setTimeout(() => {
          $(track).slick({
            slidesToShow: 3,
            slidesToScroll: 1,
            arrows: false,
            dots: false,
            autoplay: true,
            autoplaySpeed: 5000,
            responsive: [
              {
                breakpoint: 1024,
                settings: {
                  slidesToShow: 2,
                },
              },
              {
                breakpoint: 420,
                settings: {
                  slidesToShow: 1,
                },
              },
            ],
          });

          // Show carousel after initialization
          el.style.visibility = "visible";
        }, 0);
      })
      .catch((err) => {
        console.error("Errore nel recupero recensioni:", err);
        track.innerHTML =
          "<p style='color:white;'>Errore nel caricamento delle recensioni.</p>";
      });
  });
});
